import { useState, useCallback, useEffect, useRef, useMemo } from "react";
import { Button } from "../styles";
import styled from "styled-components";
import { ASSET_URL } from "../constants";

export default function Viewer360({ position, selectedDesign }) {
  const [gender, setGender] = useState("male");
  const [current, setCurrent] = useState(0);
  const stageRef = useRef(null);
  const preloadCache = useRef(new Set());
  const pos = position ? position.toLowerCase() : "front";
  const designName = selectedDesign?.designName ?? "";

  // Build URL lists once and reuse (male + female)
  const urls = useMemo(() => {
    const base = `${ASSET_URL}/designs/modal`;
    return {
      male: [`${base}/male/${pos}-${designName}`, `${base}/male/${pos}-logo`],
      female: [`${base}/female/${pos}-${designName}`, `${base}/female/${pos}-logo`],
    };
  }, [pos, designName]);

  // Active frame list for the current gender
  const frames = urls[gender];
  const total = frames.length;

  const show = useCallback(
    (i) => {
      if (!total) return;
      const idx = ((i % total) + total) % total; // wrap both ends
      setCurrent(idx);
    },
    [total]
  );

  const toggleGender = () => setGender((g) => (g === "male" ? "female" : "male"));

  const next = useCallback(() => show(current + 1), [current, show]);
  const prev = useCallback(() => show(current - 1), [current, show]);

  const onKeyDown = (e) => {
    if (e.key === "ArrowRight") {
      e.preventDefault();
      next();
    } else if (e.key === "ArrowLeft") {
      e.preventDefault();
      prev();
    }
  };

  // --- Preload helper (AVIF first, JPG fallback) ---
  const prime = useCallback((urlBase) => {
    const tryLoad = (src) => {
      if (preloadCache.current.has(src)) return;
      const img = new Image();
      img.decoding = "async";
      img.onload = () => preloadCache.current.add(src);
      img.src = src;
    };
    tryLoad(`${urlBase}.avif`);
    tryLoad(`${urlBase}.jpg`);
  }, []);

  // Preload both genders so toggle is instant
  useEffect(() => {
    [...urls.male, ...urls.female].forEach(prime);
  }, [urls, prime]);

  const onImgDragStart = (e) => e.preventDefault();

  return (
    <Container>
      <Stage ref={stageRef} tabIndex={0} aria-label="360 degree t-shirt viewer" aria-roledescription="carousel" onKeyDown={onKeyDown}>
        <picture>
          <source srcSet={`${frames[current]}.avif`} type="image/avif" />
          <img
            src={`${frames[current]}.jpg`}
            width="140"
            draggable={false}
            onDragStart={onImgDragStart}
            fetchpriority="high"
            decoding="sync"
            alt="T-shirt preview"
          />
        </picture>
      </Stage>

      <PaddlesRow>
        <button type="button" className="prev-button" onClick={prev} aria-label="Previous image">
          <i className="fa-solid fa-arrow-left" />
        </button>
        <button type="button" className="next-button" onClick={next} aria-label="Next image">
          <i className="fa-solid fa-arrow-right" />
        </button>
      </PaddlesRow>

      <div className="gender-switch">
        <Button className="yellow small" onClick={toggleGender} aria-label="Toggle gender">
          <i className="fa-solid fa-shirt"></i>
        </Button>
      </div>
    </Container>
  );
}

// === styled-components ===
const Container = styled.div`
  margin: 0 auto;
  position: relative;
  .gender-switch {
    position: absolute;
    bottom: 10px;
    right: 10px;
    button {
      margin: 10px 0 0 auto;
      padding: 6px;
      border-radius: 5px;
    }
  }
`;

const Stage = styled.div`
  position: relative;
  width: 100%;
  aspect-ratio: 1 / 1;
  border-radius: 5px;
  overflow: hidden;
  display: grid;
  place-items: center;
  user-select: none;
  cursor: default;
  outline: none;
  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    user-select: none;
    pointer-events: none;
  }
  &:focus-visible {
    box-shadow: 0 0 0 2px #ffffff;
    outline: 2px solid var(--orange-highlight);
    outline-offset: 2px;
  }
`;

const PaddlesRow = styled.div`
  position: absolute;
  top: calc(50% - 10px);
  left: 0;
  width: 100%;
  height: 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 14px;
`;
